<script setup lang="ts">
import { api } from './api/client';
import { applySiteMeta, type SiteMeta } from './utils/site-meta';
import { computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import AppHeader from './components/AppHeader.vue';
import AppFooter from './components/AppFooter.vue';
import CookieBanner from './components/CookieBanner.vue';
import ToastHost from './components/ToastHost.vue';
import { useAuthStore } from './stores/auth';
import { useFavoritesStore } from './stores/favorites';
import { useCartStore } from './stores/cart';
import { useSettingsStore } from './stores/settings';
import { configureGoogleTags } from './utils/gtag';

const auth = useAuthStore();
const favorites = useFavoritesStore();
const cart = useCartStore();
const settings = useSettingsStore();
const route = useRoute();

const chromeless = computed(() => Boolean(route.meta.chromeless));

// Admin-edited <head> (CMS → Meta / SEO). A failure keeps index.html's defaults.
async function loadSiteMeta() {
  try {
    applySiteMeta(document, (await api.get<SiteMeta>('/meta')).data);
  } catch {
    // index.html already carries usable defaults
  }
}

onMounted(async () => {
  void loadSiteMeta();
  await Promise.all([auth.fetchUser(), settings.fetch()]);
  configureGoogleTags(settings.flags.googleTagIds);
  cart.setUser(auth.user?.id ?? null);
  if (auth.isAuthenticated) await favorites.fetch();
});

// Track the actual user ID, not just is-logged-in. If user A signs out and
// user B signs in back-to-back the boolean would flicker false→true, but the
// id transition (A → null → B, or A → B directly when registering without
// explicit logout) is what tells the cart and favorites stores to swap
// their per-user data and not leak the previous session.
watch(
  () => auth.user?.id ?? null,
  (newId) => {
    cart.setUser(newId);
    // Always reset before re-fetching — otherwise favorites.fetch()'s `loaded`
    // guard early-returns and the previous user's IDs stay in the store.
    favorites.reset();
    if (newId) void favorites.fetch();
  },
);
</script>

<template>
  <div class="min-h-screen flex flex-col bg-[#fdf8f8] text-[#1c1b1b]">
    <AppHeader v-if="!chromeless" />
    <main class="flex-1">
      <RouterView />
    </main>
    <AppFooter v-if="!chromeless" />
    <CookieBanner />
    <ToastHost />
  </div>
</template>
